import type { Pair, TokenInfo } from '@/types'

interface PathResult {
  bestAmount: number
  bestPairPath: Pair[]
  bestTokenPath: TokenInfo[]
}

/** Find pair by two tokens */
export function getPair(coin1: { contract: string; coin: string }, coin2: { contract: string; coin: string }, pairs: Pair[]): Pair | undefined {
  const id0 = `${coin1.contract}-${coin1.coin}`
  const id1 = `${coin2.contract}-${coin2.coin}`
  return pairs.find(
    (v) => (v.token0.tokenId === id0 && v.token1.tokenId === id1) || (v.token0.tokenId === id1 && v.token1.tokenId === id0),
  )
}

/** Get pair ID by two tokens */
export function getPairId(coin1: { contract: string; coin: string }, coin2: { contract: string; coin: string }, pairs: Pair[]): number | null {
  const pair = getPair(coin1, coin2, pairs)
  return pair ? pair.id : null
}

/** Get TTR/USDT pair */
export function getPairTTR(pairs: Pair[]): Pair | null {
  const pair = findPair('token.ttr-TTR', 'tethertether-USDT', pairs)
  return pair ?? null
}

/**
 * Calculate optimal swap path with AMM formula.
 * way=1: given input, calculate output
 * way=2: given output, calculate input
 */
export function getPath(
  fromTokenId: string,
  toTokenId: string,
  amount: number,
  fee: number,
  pairs: Pair[],
  way: 1 | 2 = 1,
  multipath = false,
): PathResult {
  const paths: Pair[][] = []

  // Step 1: direct pair
  const direct = findPair(fromTokenId, toTokenId, pairs)
  if (direct) paths.push([direct])

  // Step 2: multi-hop via bridge tokens (EOS, USDT)
  if (multipath) {
    const eosPath = findPairs(fromTokenId, toTokenId, 'eosio.token-EOS', pairs)
    if (eosPath) paths.push(eosPath)

    const usdtPath = findPairs(fromTokenId, toTokenId, 'tethertether-USDT', pairs)
    if (usdtPath) paths.push(usdtPath)
  }

  if (paths.length === 0) {
    return { bestAmount: 0, bestPairPath: [], bestTokenPath: [] }
  }

  let bestPairPath: Pair[] | null = null
  let maxOutput = -1

  for (const path of paths) {
    let output = amount
    let nextTokenId = fromTokenId

    for (const item of path) {
      const fromToken = nextTokenId === item.token0.tokenId ? item.token0 : item.token1
      const toToken = nextTokenId === item.token0.tokenId ? item.token1 : item.token0
      nextTokenId = toToken.tokenId

      const fromReserve = getAmount(fromToken.reserve)
      const toReserve = getAmount(toToken.reserve)

      if (fromReserve === 0 || toReserve === 0) {
        output = 0
        break
      }

      if (output === 0) {
        output = fromReserve !== 0 ? toReserve / fromReserve : 0
      } else {
        output = way === 1
          ? getOutputAmount(output, fromReserve, toReserve, fee)
          : getInputAmount(output, toReserve, fromReserve, fee)
      }
    }

    if (output > maxOutput) {
      maxOutput = output
      bestPairPath = path
    }
  }

  return {
    bestAmount: maxOutput,
    bestPairPath: bestPairPath!,
    bestTokenPath: getTokenPath(fromTokenId, bestPairPath!),
  }
}

/** Parse amount from reserve string "1.0000 EOS" */
function getAmount(reserve: string): number {
  return Number(reserve.split(' ')[0])
}

/** AMM constant product: calculate output given input */
function getOutputAmount(inputAmount: number, inputReserve: number, outputReserve: number, fee: number): number {
  const tradeFee = inputAmount * fee
  const inputAmountWithFee = inputAmount - tradeFee
  const numerator = inputAmountWithFee * outputReserve
  const denominator = inputReserve + inputAmountWithFee
  return numerator / denominator
}

/** AMM constant product: calculate input given desired output */
function getInputAmount(outputAmount: number, inputReserve: number, outputReserve: number, fee: number): number {
  const numerator = outputAmount * inputReserve
  const denominator = outputReserve - outputAmount
  const inputAmountWithFee = numerator / denominator
  return inputAmountWithFee / (1 + fee)
}

/** Find two-hop path via bridge token */
function findPairs(tokenId0: string, tokenId1: string, bridgeTokenId: string, pairs: Pair[]): Pair[] | null {
  if (tokenId0 !== bridgeTokenId && tokenId1 !== bridgeTokenId) {
    const item0 = findPair(tokenId0, bridgeTokenId, pairs)
    if (item0) {
      const item1 = findPair(tokenId1, bridgeTokenId, pairs)
      if (item1) return [item0, item1]
    }
  }
  return null
}

/** Find a pair matching two token IDs */
function findPair(id0: string, id1: string, pairs: Pair[]): Pair | undefined {
  return pairs.find(
    (v) => (v.token0.tokenId === id0 && v.token1.tokenId === id1) || (v.token0.tokenId === id1 && v.token1.tokenId === id0),
  )
}

/** Build token path from pair path */
function getTokenPath(fromTokenId: string, pairPath: Pair[]): TokenInfo[] {
  const path: TokenInfo[] = []
  let lastToken: TokenInfo | null = null

  for (const p of pairPath) {
    if (lastToken === null) {
      lastToken = p.token0.tokenId === fromTokenId ? p.token0 : p.token1
      path.push(lastToken)
    }
    if (p.token0.tokenId === lastToken.tokenId) {
      lastToken = p.token1
    } else {
      lastToken = p.token0
    }
    path.push(lastToken)
  }
  return path
}
